import './LineItemLinkCell.scss';

import React, { memo, useCallback } from 'react';

import { TRAFFICKING_SEARCH_PARAMS, TraffickingTableName } from '../../../../../constants';
import { useFiltersContext } from '../../../../../contexts/FilterContext';
import { useFeatureFlags } from '../../../../../hooks/useFeatureFlags';
import { usePendingLineItemDrawer } from '../../contexts';

type LineItemLinkCellProps = {
  lineItemId: string;
  displayValue: string;
};

const LineItemLinkCell = ({ lineItemId, displayValue }: LineItemLinkCellProps): JSX.Element => {
  const { adsVsmLineItemNavigationLinks } = useFeatureFlags();
  const { applyFilter } = useFiltersContext();
  const { setPendingLineItemId } = usePendingLineItemDrawer();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>): void => {
      // The table row's own onClick toggles its selection, and CellElement only stops
      // propagation on columns that are not row-selectable. Without this the navigation click
      // also reaches the row and toggles it — so navigating away from a selected ad deselected
      // that ad, and with cascading selection on it took the ad's subtree with it.
      e.stopPropagation();

      setPendingLineItemId(lineItemId);
      applyFilter(TRAFFICKING_SEARCH_PARAMS.SELECTED_TAB, TraffickingTableName.lineItems);
    },
    [applyFilter, lineItemId, setPendingLineItemId]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Enter' || e.key === ' ') handleClick(e);
    },
    [handleClick]
  );

  if (!adsVsmLineItemNavigationLinks) {
    return <span title={displayValue}>{displayValue}</span>;
  }

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="line-item-link-cell"
      tabIndex={-1}
      data-focusable
      data-testid="line-item-link-cell"
    >
      <span title={displayValue} className="line-item-link-cell__text">
        {displayValue}
      </span>
    </div>
  );
};

export default memo(LineItemLinkCell);
